import { FunctionComponent, SVGProps } from "react";

import { IListItem } from "../../SolarGikLib/Lists/ListModels";
import { UserAccessType } from "../user/Models";
import type { RequestState } from "../app/store/RequestState";

export const McsCommandNotImplementedStatusCode = 501;

export interface ITrackersCommandResultsMap {
  [key: string]: number;
}

export interface ITrackersCommandResponse {
  results: ITrackersCommandResultsMap;
}

export enum TrackersStateEnum {
  Hibernating = -3,
  NotAvailable = -2,
  Disconnected = -1,
  Undefined = 0,
  Init = 1,
  Homing = 2,
  Manual = 3,
  Tracking = 4,
  SmartTracking = 5,
  Sleep = 6,
  Safe = 7,
  Error = 8,
  Done = 9,
  InProgress = 10,
  Updater = 11,
  Snow = 12,
  WindySnow = 13,
}

export enum SendTrackersToMaintenanceResultCodeEnum {
  Unknown = 0,
  Success = 1,
  NoSuchTracker = 2,
  UnexpectedError = 3,
  TrackerCurrentlyNotConnectedButAddedToMaintenanceList = 4,
  TrackerInSafeButAddedToMaintenanceList = 5,
}

export enum SendTrackersToFreezeResultCodeEnum {
  Unknown = 0,
  Success = 1,
  NoSuchTracker = 2,
  UnexpectedError = 3,
  TrackerCurrentlyNotConnectedCouldNotFreeze = 4,
}

export enum SendTrackersToMaxElevationResultCodeEnum {
  Unknown = 0,
  Success = 1,
  NoSuchTracker = 2,
  UnexpectedError = 3,
  TrackerCurrentlyNotConnectedButAddedToMaintenanceList = 4,
  TrackerInSafeButAddedToMaintenanceList = 5,
}

export enum RecalibrateTrackersElevationResultCodeEnum {
  Unknown = 0,
  Success = 1,
  NoSuchTracker = 2,
  UnexpectedError = 3,
  TrackerCurrentlyNotConnectedCouldNotForceElevation = 4,
  TrackerCurrentlyNotConnectedUnableToSendToHoming = 5,
  SafeModeIsActiveUnableToSendToHoming = 6,
  TrackerDidNotFinishHomingCommissioningUnableToSendToHoming = 7,
  TrackerInMaintenanceUnableToSendToHoming = 8,
}

export enum ReleaseTrackersFromMaintenanceResultCodeEnum {
  Unknown = 0,
  Success = 1,
  NoSuchTracker = 2,
  UnexpectedError = 3,
  TrackerCurrentlyNotConnectedButReleasedFromToMaintenanceList = 4,
  TrackerWasNotInMaintenance = 5,
}

export enum ResetTrackersResultCodeEnum {
  Unknown = 0,
  Success = 1,
  NoSuchTracker = 2,
  UnexpectedError = 3,
  TrackerCurrentlyNotConnectedCouldNotReset = 4,
}

export enum SendTrackersToHomingResultCodeEnum {
  Unknown = 0,
  Success = 1,
  NoSuchTracker = 2,
  UnexpectedError = 3,
  TrackerCurrentlyNotConnectedUnableToSendToHoming = 4,
  SafeModeIsActiveUnableToSendToHoming = 5,
  TrackerDidNotFinishHomingCommissioningUnableToSendToHoming = 6,
  TrackerInMaintenanceUnableToSendToHoming = 7,
}

export enum ForceTrackersElevationResultCodeEnum {
  Unknown = 0,
  Success = 1,
  NoSuchTracker = 2,
  UnexpectedError = 3,
  ElevationSavedButNotSentToTracker = 4,
}

export enum TrackersCommandType {
  ReleaseTrackersFromMaintenance = "Release",
  SendTrackersToMaintenance = "Manual",
  SendTrackersToFreeze = "Freeze",
  ResetTrackers = "Reset",
  SendTrackersToHoming = "Homing",
  ForceTrackersElevation = "Set Actual Elevation",
  SendTrackersToMaxElevation = "Send to max elevation",
  RecalibrateTrackersElevation = "Recalibrate elevation",
}

export enum TrackersCommandReasonType {
  HighWindWeatherProtection = "Weather Protection High Wind",
  HighSnowWeatherProtection = "Weather Protection High Snow",
  PanelWashing = "Panel Washing",
  TrackerMaintenance = "Tracker Maintenance",
  ClientMaintenance = "Client Maintenance",
  SoftwareUpgrade = "Software Upgrade",
}

export enum MaintenanceReason {
  WeatherProjectionWind = 1,
  WeatherProjectionSnow = 2,
  PanelWashing = 3,
  TrackerMaintenance = 4,
  UserMaintenance = 5,
  SoftwareUpdate = 6,
}

export enum ChargingStateEnum {
  Undefined = 0,
  Unknown = 1,
  Charging = 2,
  UsingBattery = 3,
  NotCharging = 4,
}

export interface ITrackersState {
  trackersStatus: ISiteToTrackersState;
  loading: boolean;
  loadingBySite?: Record<string, boolean>;
  requestsBySite?: Record<string, RequestState>;
  selectedTrackersIds: number[];
}

export interface ISiteToTrackersState {
  [siteId: string]: ITrackerStatus[];
}

export interface ITagValue<valueType> {
  value: valueType;
  time: number;
}

export interface ITrackerStatus {
  updateTime: number;
  id: number;
  name: string;
  currentState?: number;
  uptime?: ITagValue<number>;
  targetState?: ITagValue<number>;
  currentElevation?: ITagValue<number>;
  targetElevation?: ITagValue<number>;
  shadingRatio?: ITagValue<number>;
  rssi?: ITagValue<number>;
  chargingState?: ITagValue<ChargingStateEnum>;
  chargePercentage?: ITagValue<number>;
  hasForcedElevation: boolean;
  lastHomingTime?: ITagValue<number>;
  minPhysicalElevation?: number;
  maxPhysicalElevation?: number;
  minTrackingElevation?: number;
  maxTrackingElevation?: number;
}

export interface ITrackerMetadata {
  macAddress: string;
  ip: string;
  xPosition: number;
  yPosition: number;
}

export enum ElevationDirections {
  West = "West",
  East = "East",
}

export interface TrackerElevation {
  value: number;
  direction: ElevationDirections;
}

export enum HomingDirection {
  West = 0,
  East = 1,
}

export interface IMaintenanceListitem extends IListItem {
  title: string;
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  accessType: UserAccessType;
}

export interface TrackerDesignMetadata {
  name: string | null;
  deviceId: number;
  localCoordinates: { localEast?: number; localNorth?: number; localHeight?: number };
  associatedApId?: number | null;
  installationAzimuthDegrees?: number;
  slopeElevationDegrees?: number;
  trackerModel: { sgCatalogNumber: string; totalNumberOfPanels: number };
  powerSource?: TrackerPowerSourceDesignMetadata | null;
}

export interface FixedTiltPvTableModel {
  sgCatalogNumber: string;
  widthInMeters?: number | null;
  numberOfPanelsAlongWidth: number;
}

export interface FixedTiltPvTablePanelRow {
  ordinal: number;
  startOffsetMeters: number;
  lengthInMeters: number;
  numberOfPanels: number;
}

export interface FixedTiltPvTableDesignMetadata {
  deviceId: number;
  name: string | null;
  serialNumber: string;
  localCoordinates: TrackerDesignMetadata["localCoordinates"] | null;
  tableYawDegrees?: number | null;
  panelTiltDegrees?: number | null;
  lengthInMeters?: number | null;
  numberOfPanelsAlongLength?: number | null;
  panelRows?: FixedTiltPvTablePanelRow[] | null;
  model: FixedTiltPvTableModel;
  dcCollectionStringDeviceId?: number | null;
  inverterDeviceId?: number | null;
}

export interface AccessPointDesignMetadata {
  name: string | null;
  deviceId: number;
  localCoordinates: TrackerDesignMetadata["localCoordinates"];
  model: string;
}

export interface ControlCabinetDesignMetadata {
  deviceId: number;
  cabinetType?: string | null;
  localCoordinates: TrackerDesignMetadata["localCoordinates"];
  powerCircuits: PowerCircuitDesignMetadata[];
}

export interface PowerDistributionUnitDesignMetadata {
  serialNumber: string;
  localCoordinates: TrackerDesignMetadata["localCoordinates"];
  powerCircuits: PowerCircuitDesignMetadata[];
}

export interface PowerCircuitDesignMetadata {
  deviceId: number;
  name: string;
  trackerDeviceIds: number[];
}

export interface DcCollectionStringDesignMetadata {
  deviceId: number;
  trackerDeviceIds: number[];
}

export interface InverterDesignMetadata {
  deviceId: number;
  serialNumber: string;
  localCoordinates: TrackerDesignMetadata["localCoordinates"] | null;
  dcCollectionStrings: DcCollectionStringDesignMetadata[];
}

export interface TrackerPowerSourceDesignMetadata {
  sourceType: string;
  sourceName: string;
  sourceDeviceId?: number | null;
  sourceSerialNumber?: string | null;
  circuitDeviceId: number;
  circuitName: string;
}

export interface WeatherStationDesignMetadata {
  deviceId: number;
  name: string | null;
  localCoordinates: TrackerDesignMetadata["localCoordinates"];
}

export interface CameraDesignMetadata {
  id: number;
  agentId: number | null;
  name: string;
  model: string;
  localCoordinates: TrackerDesignMetadata["localCoordinates"] | null;
  weatherStationDeviceId: number | null;
}

export interface SiteDesignMetadata {
  trackers: TrackerDesignMetadata[];
  fixedTiltPvTables: FixedTiltPvTableDesignMetadata[];
  accessPoints: AccessPointDesignMetadata[];
  controlCabinets: ControlCabinetDesignMetadata[];
  powerDistributionUnits: PowerDistributionUnitDesignMetadata[];
  inverters: InverterDesignMetadata[];
  weatherStations: WeatherStationDesignMetadata[];
  cameras: CameraDesignMetadata[];
}

export type AuxDeviceStatus = "Online" | "Malfunction" | "Unknown";

export interface AuxStatusItem<TId extends number | string = number | string> {
  id: TId;
  status: AuxDeviceStatus;
  statusTime: number;
  malfunctionDescription?: string | null;
}

export interface AuxDevicesStatus {
  accessPoints: AuxStatusItem<number>[];
  lccs: AuxStatusItem<number>[];
  mcs: AuxStatusItem<number>[];
  pdus: AuxStatusItem<string>[];
  inverters: InverterStatusItem[];
  weatherStations: AuxStatusItem<number>[];
  cameras: AuxStatusItem<number>[];
}

export interface InverterStatusItem {
  id: number;
  status: AuxDeviceStatus;
  statusTime: number | null;
  rawStatusName: string | null;
  details?: string | null;
}
